import useSupported from '../useSupported/useSupported.svelte.js';
import { extract, isNullish } from '@vpap_svelte/internal';
// from https://web.dev/articles/device-pixel-content-box#feature_detection
async function hasDevicePixelContentBox() {
    return new Promise((resolve) => {
        const ro = new ResizeObserver((entries) => {
            resolve(entries.every((entry) => 'devicePixelContentBoxSize' in entry));
            ro.disconnect();
        });
        ro.observe(document.body, { box: 'device-pixel-content-box' });
    }).catch(() => false);
}
/**
 * Returns a reactive value holding the size of an element, either in CSS pixels or in device pixels (with options.method === "dpi").
 *
 */
export class ElementSize {
    #size = $state({
        width: 0,
        height: 0,
    });
    #isInitial = $state(true);
    constructor(element, options = { initialSize: { width: 0, height: 0 }, box: 'border-box' }) {
        this.#size = {
            width: options.initialSize?.width ?? 0,
            height: options.initialSize?.height ?? 0,
        };
        let baseDpiSupport = useSupported(hasDevicePixelContentBox);
        let supportsDpi = $derived(baseDpiSupport.isSupported);
        let el = $derived(extract(element));
        const boxType = options.box ?? 'content-box';
        $effect(() => {
            if (isNullish(el) || supportsDpi === undefined)
                return;
            const ro = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const box = boxType === 'border-box'
                        ? entry.borderBoxSize
                        : boxType === 'content-box'
                            ? entry.contentBoxSize
                            : boxType === 'device-pixel-content-box' && supportsDpi === true
                                ? entry.devicePixelContentBoxSize
                                : entry.contentBoxSize;
                    // modern browsers all represent `box` as an array, but older versions of Firefox represent it as a single value
                    // https://caniuse.com/mdn-api_resizeobserverentry_contentboxsize
                    const boxArr = Array.isArray(box) ? box : [box];
                    let width = boxArr.reduce((acc, size) => Math.max(acc, size.inlineSize), 0);
                    let height = boxArr.reduce((acc, size) => Math.max(acc, size.blockSize), 0);
                    if (boxType === 'device-pixel-content-box' && supportsDpi !== true) {
                        const dpr = window.devicePixelRatio || 1;
                        width = Math.round(width * dpr);
                        height = Math.round(height * dpr);
                    }
                    this.#size.width = width;
                    this.#size.height = height;
                    this.#isInitial = false;
                }
            });
            const roBoxMethod = boxType === 'device-pixel-content-box' && supportsDpi !== true ? 'content-box' : boxType;
            ro.observe(el, { box: roBoxMethod });
            return () => {
                ro.disconnect();
            };
        });
    }
    get width() {
        return this.#size.width;
    }
    get height() {
        return this.#size.height;
    }
    get isInitial() {
        return this.#isInitial;
    }
}
